import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { SdfBadge, SdfButton } from '@synerg/react-components';
import { AuthGrid } from './AuthGrid';
import { getFormattedDate } from '../../../common';

const AVAILABLE_PAGE_SIZES = [];

const PAGE_SIZES = [
	{ label: '10', value: 10 },
	{ label: '20', value: 20 },
	{ label: '40', value: 40 },
	{ label: '60', value: 60 },
	{ label: '80', value: 80 },
	{ label: '100', value: 100 }
];

export const DeniedAuthGrid = (props) => {
	const { selectedEnvironment, data, getSubscriberDetails, searchedText, searchedStatus } = props;

	const [ deniedAuthList, setDeniedAuthList ] = useState([]);
	const [ pageNo, setPageNo ] = useState(1);
	const [ pageSize, setPageSize ] = useState(10);
	const [ sortOrder, setSortOrder ] = useState({ colId: 'organizationName', sortBy: 'asc' });
	const [ pageTableData, setPageTableData ] = useState([]);

	useEffect(() => {
		if (selectedEnvironment === "PROD") {
			setDeniedAuthList(
				get(data, "prod.subscriptions.deniedAuth.subscriptionList", [])
			);
		} else {
			setDeniedAuthList(
				get(data, "subscriptions.deniedAuth.subscriptionList", [])
			);
		}
	}, [data, selectedEnvironment]);

	useEffect(() => {
		let pageData = deniedAuthList;

		if (sortOrder.colId === 'consentDateTime') {
			// eslint-disable-next-line array-callback-return
			pageData = pageData.sort((a, b) => {
				if (sortOrder.sortBy === 'desc') {
					return new Date(b.consentDateTime) - new Date(a.consentDateTime);
				} else if (sortOrder.sortBy === 'asc') {
					return new Date(a.consentDateTime) - new Date(b.consentDateTime);
				}
			});
		}
		if (sortOrder.colId === 'organizationName') {
			// eslint-disable-next-line array-callback-return
			pageData = pageData.sort((a, b) => {
				if (sortOrder.sortBy === 'desc') {
					return b[sortOrder.colId].toLowerCase().localeCompare(a[sortOrder.colId].toLowerCase());
				} else if (sortOrder.sortBy === 'asc') {
					return a[sortOrder.colId].toLowerCase().localeCompare(b[sortOrder.colId].toLowerCase());
				}
			});
		}
		setPageTableData(pageData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deniedAuthList, sortOrder]);

	useEffect(() => {
		AVAILABLE_PAGE_SIZES.length = 0;
		if (pageTableData.length) {
			PAGE_SIZES.every((element) => {
				if (pageTableData.length > element.value) {
					AVAILABLE_PAGE_SIZES.push(element);
					return true;
				}
				else {
					AVAILABLE_PAGE_SIZES.push(element);
					return false;
				}
			})
		}
		setPageSize(10);
	}, [pageTableData]);

	const total = pageTableData.length;
	const start = total > 0 ? (pageNo - 1) * pageSize + 1 : 0;
	const end = (pageNo * pageSize) > total ? total : (pageNo * pageSize);
	let totalPages = 1;
	if (total % pageSize === 0 && total !== 0) {
		totalPages = (total - total % pageSize) / pageSize;
	} else {
		totalPages = ((total - total % pageSize) / pageSize) + 1;
	}
	let paginatedData = pageTableData.slice((pageNo - 1) * pageSize, pageNo * pageSize);

	useEffect(() => {
		setPageNo(1);
	}, [pageSize, searchedText, searchedStatus]);

	const getStatus = (text) => {
		switch (text) {
			case "Active":
				return "complete";
			case "Cancelled":
				return "failed";
			case "Suspended":
				return "changed";
			default:
				return "default";
		}
	};

	const getNameAndOOID = (rowData = {}) => {
		return (
			<React.Fragment>
				<div
					className="text-body-01 overflow-ellipsis truncate"
					title={rowData.organizationName}
				>
					{rowData.organizationName}
				</div>
				<div className="text-xs leading-sm">{rowData.ooid}</div>
			</React.Fragment>
		);
	};

	const getSubscription = (row = {}) => {
		return (
			<SdfBadge
				spacing="default"
				status={getStatus(row.subscriptionStatusCode)}
			>
				{row.subscriptionStatusCode}
			</SdfBadge>
		);
	};

	const getStatusText = (row = {}) => {
		return (
			<div>
				{`${
					row.consentDateTime
						? "REQUESTED " + getFormattedDate(new Date(row.consentDateTime))
						: "N/A"
				}`}
			</div>
		);
	};

	const getSubscribeInfo = (row = {}) => {
		return (
			<SdfButton
				emphasis={"primary"}
				variant="text"
				className="whitespace-nowrap"
				onClick={() => getSubscriberDetails(row.ooid + "/" + row.consenterAoid)}
			>
				Subscriber info
			</SdfButton>
		);
	};

	const getColWidth = (width) => {
		const container = document.getElementById("subscription-container");
		return (((container && container.offsetWidth) || 1000) * width) / 100;
	};

	const cols = [
		{
			field: "organizationName",
			headerName: "Client name and OOID",
			sort: true,
			cellRenderer: (event) => getNameAndOOID(event.data),
			toolPanelClass: "p-20",
			width: getColWidth(50),
			flex: 3.25
		},
		{
			field: "subscriptionStatusCode",
			headerName: "Subscription",
			sort: false,
			cellRenderer: (event) => getSubscription(event.data),
			toolPanelClass: "p-10",
			width: getColWidth(14),
			textCenter: true,
			flex: 0.9
		},
		{
			field: "consentDateTime",
			headerName: "Status",
			sort: true,
			cellRenderer: (event) => getStatusText(event.data),
			toolPanelClass: "p-10",
			width: getColWidth(21),
			flex: 1
		},
		{
			field: "ooid",
			headerName: "",
			sort: false,
			cellRenderer: (event) => getSubscribeInfo(event.data),
			toolPanelClass: "p-10",
			width: getColWidth(15),
			textCenter: true,
			flex: 0.8
		}
	];

	return (
		<div className="w-full mt-10">
			<div className="text-heading-04 font-black">
				Denied authorization ({pageTableData.length})
			</div>
			<div className="flex justify-between mt-2">
				<div className="text-body-02">
					Client authorization requests were denied by customer(s) listed below:
				</div>
			</div>
			<div className="w-full mt-6">
				<AuthGrid
					data={paginatedData}
					availablePageSizes={AVAILABLE_PAGE_SIZES}
					{...{ cols, start, end, pageNo, pageSize, setSortOrder, setPageNo, setPageSize, totalPages }}
				/>
			</div>
		</div>
	);
};
